import React, { FC, useMemo, useState } from 'react'
import Grid from '@material-ui/core/Grid'
import { alphabeticalSort, ApplicationFormElement, AssignmentData, CallPartitionResource, ELEMENT_TYPES, ElementValue, FORM_ELEMENT_TYPES, FormElement, Input, Item, NewButton } from 'prace-common-components'
import { $Container, $Grid, $Label, $ModalContainer, $Notice, $Text } from './styles'

export type AdminEditChange = { values?: string[]; file?: File; reason: string }

type Props = {
  /* Element definition, with select items already resolved by the form */
  element: ApplicationFormElement,
  fieldLabel: string,
  /* The stored answer, undefined for a field that was never answered */
  data?: AssignmentData,
  /* The application cutoff's resources, the partitions a partition select can offer */
  resources: CallPartitionResource[],
  allPartitions: { id: string; name: string }[],
  loading: boolean,
  onConfirm: (change: AdminEditChange) => void,
  onCancel: () => void,
}

const ARRAY_TYPES = [FORM_ELEMENT_TYPES.MULTISELECT, FORM_ELEMENT_TYPES.MULTITEXT]

/* Same conversion the form uses when saving (CallApplication onChangeValue) */
const toValues = (value: ElementValue): string[] => {
  const toSave = value === null || value === undefined ? '' : value
  return Array.isArray(toSave) ? toSave.map(String) : [String(toSave)]
}

/* Confirmation step for an admin correction of one submitted answer: shows the field, the current
   and the new value, and takes an optional reason. Nothing is sent until Confirm. */
export const AdminEditValueModal: FC<Props> = (
  { element, fieldLabel, data, resources, allPartitions, loading, onConfirm, onCancel },
) => {
  const elementType = element.elementType as FORM_ELEMENT_TYPES
  const isUpload = elementType === FORM_ELEMENT_TYPES.UPLOAD
  const isPartition = elementType === FORM_ELEMENT_TYPES.SELECT_PARTITION
  const currentValues = data?.input?.values?.map(String) || []
  const [value, setValue] = useState<ElementValue>(
    ARRAY_TYPES.includes(elementType) ? currentValues : (currentValues[0] ?? ''))
  const [file, setFile] = useState<File | null>(null)
  const [reason, setReason] = useState('')

  const items = useMemo<Item[]>(() => {
    if(!isPartition) return (element.items || []) as Item[]
    const partitions = new Map(resources
      .filter((resource) => !resource.partition?.disabled)
      .map((resource) => [resource.partition.id, resource.partition.name]))
    return alphabeticalSort([...partitions].map(([id, name]) => ({ value: id, label: name }) as Item), 'label')
  }, [isPartition, element.items, resources])

  const labelOf = (stored: string) => {
    const item = items.find((option) => String(option.value) === stored)
    if(item) return String(item.label)
    return (isPartition && allPartitions.find((p) => String(p.id) === stored)?.name) || stored
  }
  const currentDisplay = isUpload
    ? data?.upload?.originalFilename || ''
    : currentValues.filter((v) => v !== '').map(labelOf).join(', ')

  const newValues = toValues(value)
  /* Empty strings are not answers: an untouched never-answered field reads [''] against [] */
  const answers = (values: string[]) => JSON.stringify(values.filter((v) => v !== ''))
  const changed = isUpload ? !!file : answers(newValues) !== answers(currentValues)

  const editor = {
    ...element,
    id: `admin-edit-${element.id}`,
    type: ELEMENT_TYPES.ELEMENT,
    elementType,
    items,
    value: isUpload ? file?.name : value,
    title: 'New value',
    name: 'value',
    disabled: false,
    error: false,
    errorMsg: '',
    hideItalic: true,
    debounce: false,
    onDownloadClick: () => null,
    onUploadClick: () => null,
    onChange: (name: string, newValue: ElementValue | File) => name === 'file'
      ? setFile(newValue as unknown as File | null)
      : setValue(newValue as ElementValue),
  }

  return (
    <$ModalContainer>
      <$Grid container direction='column'>
        <Grid item>
          <$Label shrink>Field</$Label>
          <$Text>{fieldLabel}</$Text>
        </Grid>
        <Grid item>
          <$Label shrink>Current value</$Label>
          <$Text>{currentDisplay || '(empty)'}</$Text>
        </Grid>
        <Grid item>
          <FormElement element={editor as any} />
        </Grid>
        <Grid item>
          <Input
            title='Reason (optional)'
            name='reason'
            type='text'
            multiline
            minRows={2}
            hideItalic
            value={reason}
            onChange={(_name: string, newReason: Nullable<StringNumber>) => setReason(String(newReason ?? ''))}
          />
        </Grid>
        {isPartition && <$Notice>
          Requested resources are not changed with the partition. Edit them separately if they no longer apply.
        </$Notice>}
        <$Notice>
          This change will be recorded in the application&apos;s activity log with the current and the new value.
        </$Notice>
      </$Grid>
      <$Container container justifyContent='space-between'>
        <Grid item xs={3}>
          <NewButton variant='outlined' onClick={onCancel}>Cancel</NewButton>
        </Grid>
        <Grid item xs container justifyContent='flex-end'>
          <NewButton
            disabled={!changed || loading}
            loading={loading}
            onClick={() => onConfirm({ values: isUpload ? undefined : newValues, file: file || undefined, reason })}
          >
            Confirm change
          </NewButton>
        </Grid>
      </$Container>
    </$ModalContainer>
  )
}
